import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import type { RootState } from "reducers";
import { IConcreteBaseInput } from "../../domain/IConcreteBaseInput";
import { IGetConcreteBaseInputsRequestParams } from "../../domain/IGetConcreteBaseInputsRequestParams";
import { concreteBaseService } from "../../services/concreteBase.service";
import { IOutputValues } from "../../domain/IOutputValues";
import { IOutputOption } from "../../domain/IOutputOption";
import { IPartner } from "Dashboard/scenes/Partners/domain";
import { IConcreteBaseInputUpdate } from "../../domain/IConcreteBaseInputUpdate";
import { IConcreteBaseProducedItemResponse } from "../../domain/IConcreteBaseProduceItemResponse";
import { IInputItemToTake } from "../../domain/IInputItemToTake";
import { IInputsForProduction } from "../../domain/IInputsForProduction";
import { toast } from "react-toastify";
import { IGetPageRequestParams, IUpdateStateAfterSearchParams } from "hooks/pagingAndSort";

export const getInputs = createAsyncThunk(
  "ConcreteBase/getConcreteBaseInputs",
  async (
    params: {
      getInputsRequestParams: IGetConcreteBaseInputsRequestParams,
      pagingParams: IGetPageRequestParams,
      abortSignal: AbortSignal,
      updateStateAfterFetch: (updateStateParams: IUpdateStateAfterSearchParams) => void
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await concreteBaseService.getConcreteBaseInputs(
        params.getInputsRequestParams,
        params.pagingParams,
        params.abortSignal
      );
      return {
        response,
        pagingData: params.pagingParams,
        updateStateAfterFetch: params.updateStateAfterFetch
      };
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const updateConcreteBaseInput = createAsyncThunk(
  "ConcreteBase/updateInput",
  async (data: IConcreteBaseInputUpdate, { rejectWithValue }) => {
    try {
      return await concreteBaseService.updateConcreteBaseInput(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

interface IUpdateInputItemPopoverState {
  isOpen: boolean;
  top?: number;
  left?: number;
  offsetTop?: number;
  offsetLeft?: number;
  itemRowIndex: number;
  focus?: string;
}

interface inputState {
  loading: boolean;
  inputs: IConcreteBaseInput[];
  selectedIndex: number;
  totalPrice: number;
  quantityAmountTotal: number;
  quantityAmountRemaining: number;
  outputValues: IOutputValues;
  productionDate?: string;
  error: {};
  deleteInputModalOpen: boolean;
  partners: IPartner[];
  selectedPartner: IPartner | undefined;
  indexForOperation: number;
  newInputPopover: IPopoverState;
  updateInputItemPopover: IUpdateInputItemPopoverState;
  searchInputPopover: IPopoverState;
  itemInventoryPopover: IPopoverState;
  ownProductPopover: IPopoverState;
  newOutputPopover: IPopoverState;
}

const initialState: inputState = {
  loading: false,
  inputs: [],
  selectedIndex: -1,
  outputValues: {
    repository: undefined,
    map: {},
  },
  totalPrice: 0,
  quantityAmountTotal: 0,
  quantityAmountRemaining: 0,
  error: {},
  deleteInputModalOpen: false,
  partners: [],
  selectedPartner: undefined,
  indexForOperation: -1,
  newInputPopover: { isOpen: false },
  updateInputItemPopover: {
    isOpen: false,
    itemRowIndex: -1,
    top: 0,
    left: 0,
  },
  searchInputPopover: { isOpen: false },
  ownProductPopover: { isOpen: false },
  newOutputPopover: { isOpen: false },
  itemInventoryPopover: { isOpen: false },
};

export const inputSlice = createSlice({
  name: "concrete-base-input",
  initialState,
  reducers: {
    reset: (state) => {
      state = initialState;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setPartners: (state, action: PayloadAction<IPartner[]>) => {
      state.partners = action.payload;
    },
    setSelectedPartnerId: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      if (action.payload) {
        let sp = state.partners.find((p) => p.partnerId === action.payload);
        state.selectedPartner = sp;
      } else {
        state.selectedPartner = undefined;
      }
    },
    setDeleteInputModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean; index: number }>
    ) => {
      state.deleteInputModalOpen = action.payload.visible;
      state.indexForOperation = action.payload.index;
    },
    setNewInputPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.newInputPopover = action.payload;
    },
    setSearchInputPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.searchInputPopover = action.payload;
    },
    setItemInventoryPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.itemInventoryPopover = action.payload;
    },
    setOwnProductPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.ownProductPopover = action.payload;
    },
    setNewOutputPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.newOutputPopover = action.payload;
    },
    setProductionDate: (state, action: PayloadAction<string | undefined>) => {
      state.productionDate = action.payload;
    },
    addNewInput: (state, action: PayloadAction<IConcreteBaseInput>) => {
      if (action.payload) {
        state.inputs = [action.payload, ...state.inputs];
        state.totalPrice += action.payload.totalPrice;
        state.quantityAmountTotal += action.payload.quantityAmountTotal;
        state.quantityAmountRemaining += action.payload.quantityAmountRemaining;
      }
    },
    produceNewInput: (state, action: PayloadAction<IConcreteBaseProducedItemResponse>) => {
      action.payload.changedInputs.forEach(changedInput => {
        for (let inputIndex = 0; inputIndex < state.inputs.length; inputIndex++) {
          if (state.inputs[inputIndex].inputId === changedInput.inputId) {
            state.inputs[inputIndex].quantityAmountRemaining = changedInput.quantityAmountRemaining;
            return;
          }
        }
      });
      state.inputs = [...action.payload.producedInputs, ...state.inputs];
      state.outputValues = {
        repository: action.payload.producedInputs[0].repository,
        map: {},
      };
      if (!action.payload.producedInputs[0].item.canStayInRepository) {
        state.outputValues.map[action.payload.producedInputs[0].inputId] = action.payload.producedInputs[0].quantityAmountRemaining;
      }
    },
    setUpdateInputItemPopover: (
      state,
      action: PayloadAction<IUpdateInputItemPopoverState>
    ) => {
      state.updateInputItemPopover = action.payload;
    },
    setCBOutputValue: (state, action: PayloadAction<IOutputOption>) => {
      const option = action.payload;
      if (option.value) {
        if (Object.keys(state.outputValues.map).length === 0) {
          state.outputValues.repository = option.repository;
          state.outputValues.map[option.key] = option.value;
        } else {
          if (
            state.outputValues.repository &&
            state.outputValues.repository.repositoryId ===
            option.repository.repositoryId
          ) {
            state.outputValues.map[option.key] = option.value;
          }
        }
      } else {
        delete state.outputValues.map[option.key];
        if (Object.keys(state.outputValues.map).length === 0) {
          delete state.outputValues.repository;
        }
      }
    },
    setOutputValues: (state, action: PayloadAction<IInputsForProduction>) => {
      if (Object.keys(state.outputValues.map).length === 0) {
        state.outputValues.repository = action.payload.repository;
        for (const input of action.payload.inputs) {
          state.outputValues.map[input.inputId] = input.quantity;
        }
      } else {
        toast.error("Neočekivana greška. Već su unete neke količine.");
      }
    },
    setSelectedIndex: (state, action: PayloadAction<number>) => {
      state.selectedIndex = action.payload;
    },
    resetOutputValue: (state) => {
      state.outputValues = { repository: undefined, map: {}, };
      state.productionDate = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getInputs.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getInputs.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        if (action.payload.pagingData.fetchMode === 'replace') {
          const elementsFetched = action.payload.response.inputs.length;
          const newInputs = action.payload.response.inputs;
          const newFirstElementId = newInputs?.[0]?.inputId;
          const newLastElementId = newInputs?.[newInputs.length - 1]?.inputId;
          state.inputs = newInputs;
          state.selectedIndex = 0;
          action.payload.updateStateAfterFetch({
            fetchSide: "replace",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else if (action.payload.pagingData.beforeId) {
          const elementsFetched = action.payload.response.inputs.length;
          const newInputs = [...action.payload.response.inputs, ...state.inputs];
          const newFirstElementId = newInputs?.[0]?.inputId;
          const newLastElementId = newInputs?.[newInputs.length - 1]?.inputId;
          state.inputs = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "beforeFirst",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else {
          const elementsFetched = action.payload.response.inputs.length;
          const newInputs = [...state.inputs, ...action.payload.response.inputs];
          const newFirstElementId = newInputs?.[0]?.inputId;
          const newLastElementId = action.payload.response.inputs?.[elementsFetched - 1]?.inputId;
          state.inputs = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "afterLast",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        }
        state.totalPrice = action.payload.response.totalPriceAllInputs;
        state.quantityAmountTotal = action.payload.response.quantityAmountTotal;
        state.quantityAmountRemaining = action.payload.response.quantityAmountRemaining;
      }
    });
    builder.addCase(getInputs.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(updateConcreteBaseInput.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateConcreteBaseInput.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        action.payload.forEach(newInput => {
          state.inputs = state.inputs.map(i => {
            if (i.inputId === newInput.inputId) {
              if (i.totalPrice !== newInput.totalPrice) {
                state.totalPrice = state.totalPrice + newInput.totalPrice - i.totalPrice;
              }
              return newInput;
            } else {
              return i;
            }
          });
          if (newInput.pricePerUnit === undefined || newInput.pricePerUnit === null || newInput.pricePerUnit === 0) {
            delete state.outputValues.map[newInput.inputId];
            if (Object.keys(state.outputValues.map).length === 0) {
              delete state.outputValues.repository;
            }
          }
        });
      }
    });
    builder.addCase(updateConcreteBaseInput.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
  },
});

export const {
  setError,
  setSelectedIndex,
  setPartners,
  setSelectedPartnerId,
  setDeleteInputModalOpen,
  setNewInputPopover,
  setSearchInputPopover,
  addNewInput,
  produceNewInput,
  setCBOutputValue,
  setOutputValues,
  setOwnProductPopover,
  setNewOutputPopover,
  setProductionDate,
  setItemInventoryPopover,
  resetOutputValue,
  setUpdateInputItemPopover,
} = inputSlice.actions;

export const selectState = (state: RootState) => state.cb.input;
export const selectConcreteBaseInputForUpdateItem = (state: RootState) =>
  state.cb.input.inputs[state.cb.input.updateInputItemPopover.itemRowIndex];

export default inputSlice.reducer;
